import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { Box, IconButton } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min.js";
import { selectAdminBidData, updateSortModel } from "../../slices/adminBid/adminBidSlice.js";
import { BID_STATUS, BID_TYPE } from "../../util/bid/bidConstants.js";
import { isAwardedBidPastTheEndDate } from "../../util/bid/shiftBidUtils";
import {
  getSortedBidsByScheduledStatus,
  getSortedBidsByStartDateAndEndDate,
  getSortedBidsByStatus,
} from "../../util/bid/sortBids.js";
import { BID_ADMIN_PAGE_TYPE, FEATURES, NON_CONTRACT } from "../../util/constants.js";
import { getFormattedTimeStamp, getShiftDate } from "../../util/dateTimeFormatter.js";
import { getLastUpdatedCustomComparator } from "../../util/sortTrades.js";
import CustomErrorTableOverlay from "../CustomErrorTableOverlay.jsx";
import CustomLoadingTableOverlay from "../CustomLoadingTableOverlay.jsx";
import CustomNoRowsTableOverlay from "../CustomNoRowsTableOverlay.jsx";
import BidAdminAction from "./BidAdminAction.jsx";
import BidScheduledStatusChip from "./BidScheduledStatusChip.jsx";
import { getUnionContractGivenWorkgroup } from "../../util/workgroupMapper.js";

const BidTable = (props) => {
  const history = useHistory();
  const [selectionModel, setSelectionModel] = React.useState([]);
  const adminBidData = useSelector(selectAdminBidData);
  const dispatch = useDispatch();

  const editBid = (bid) => {
    const bidChildItems = props.featureConfig.childItems;
    const manageShiftBidFeature = bidChildItems.find((item) => item.key === FEATURES.MANAGE_BID);

    // if (bid.endDate.ts < TWO_MONTHS_IN_PAST(props.station).ts) {
    // return setDialogOpen(true);
    // } else {
    history.push({ pathname: manageShiftBidFeature.path.replace(":referenceNumber", bid.referenceNumber) });
    // }
  };

  const renderBidType = (bid) => {
    return bid.bidType;
  };

  const renderStatus = (bid) => {
    return bid.status;
  };

  const renderActionsCell = (bid) => {
    return <BidAdminAction bid={bid} featureConfig={props.featureConfig} />;
  };

  const renderWindowDate = (bid) => {
    return bid.windowStartDate
      ? `${getShiftDate(bid.windowStartDate)} - ${getShiftDate(bid.windowEndDate)}, ${bid.windowStartDate.toFormat(
          "HH:mm ZZZZ"
        )}`
      : "";
  };

  const renderSchedDate = (bid) => {
    const schedEndDate = bid.schedEndDate ? getShiftDate(bid.schedEndDate) : "";
    return bid.schedStartDate ? `${getShiftDate(bid.schedStartDate)} - ${schedEndDate}` : "";
  };

  const renderBidTable = () => {
    const columns = [
      {
        field: "actionButton",
        headerName: "",
        renderCell: (params) => {
          return (
            <IconButton
              color="primary"
              onClick={() => {
                editBid(params.row);
              }}
              disabled={
                params.row.bidType === BID_TYPE.SHIFT &&
                params.row.status === BID_STATUS.CLOSED &&
                !params.row.s3AllEmpPermBidData &&
                getUnionContractGivenWorkgroup(params.row.workgroup) !== NON_CONTRACT
              }
            >
              {params.row.status === BID_STATUS.DELETED ||
              isAwardedBidPastTheEndDate(params.row, params.row.schedEndDate) ? (
                <VisibilityIcon />
              ) : (
                <EditIcon />
              )}
            </IconButton>
          );
        },
        flex: 0.05,
        sortable: false,
      },
      {
        field: "createdAt",
        headerName: "Last Updated",
        type: "dateTime",
        valueFormatter: (params) => {
          return getFormattedTimeStamp(params.value);
        },
        sortComparator: getLastUpdatedCustomComparator,
        flex: 0.1,
        minWidth: 140,
      },
      {
        field: "bidType",
        renderCell: (params) => {
          return renderBidType(params.row);
        },
        headerName: "Bid Type",
        type: "string",
        flex: 0.08,
        minWidth: 80,
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", flex: 0.08, minWidth: 100 },
      {
        field: "schedDates",
        valueGetter: (params) => {
          return {
            startDate: params.row.schedStartDate,
            endDate: params.row.schedEndDate,
          };
        },
        renderCell: (params) => {
          return renderSchedDate(params.row);
        },
        headerName: "Bid Effective Dates",
        type: "string",
        flex: 0.15,
        minWidth: 170,
        sortComparator: getSortedBidsByStartDateAndEndDate,
      },
      {
        field: "windowDates",
        valueGetter: (params) => {
          return {
            startDate: params.row.windowStartDate,
            endDate: params.row.windowEndDate,
          };
        },
        renderCell: (params) => {
          return renderWindowDate(params.row);
        },
        headerName: "Bid Window",
        type: "string",
        flex: 0.2,
        minWidth: 245,
        sortComparator: getSortedBidsByStartDateAndEndDate,
      },
      { field: "bidName", headerName: "Bid Name", type: "string", flex: 0.3 },
      {
        field: "status",
        valueGetter: (params) => {
          return params.value;
        },
        valueFormatter: ({ value }) => {
          return value;
        },
        renderCell: (params) => {
          return renderStatus(params.row);
        },
        headerName: "Status",
        type: "string",
        flex: 0.05,
        minWidth: 100,
        sortComparator: getSortedBidsByStatus,
      },
      {
        field: "scheduledStatus",
        valueGetter: (params) => {
          return params.row.status === BID_STATUS.PENDING ? params.value : "";
        },
        headerName: "Scheduled Status",
        flex: 0.1,
        minWidth: 130,
        renderCell: (params) => {
          return params.row.status === BID_STATUS.PENDING ? (
            <div>
              <BidScheduledStatusChip scheduledStatus={params.row.scheduledStatus} />
            </div>
          ) : (
            ""
          );
        },
        sortComparator: getSortedBidsByScheduledStatus,
      },
      {
        field: "auditHistory",
        renderCell: (params) => {
          return (
            <StickyNote2Icon
              onClick={() => props.setSelectedBidForAuditHistory(params.row)}
              color="primary"
              sx={{
                cursor: "pointer",
              }}
            />
          );
        },
        headerName: "Audit History",
        flex: 0.05,
        minWidth: 100,
        sortable: false,
      },
      {
        field: "action",
        renderCell: (params) => {
          return renderActionsCell(params.row);
        },
        headerName: "Actions",
        type: "string",
        flex: 0.1,
        minWidth: 125,
        sortable: false,
      },
    ];

    return (
      <div style={{ width: "100%", height: "inherit", paddingLeft: "2rem", paddingRight: "2rem" }}>
        <DataGridPro
          apiRef={props.tableDesktopRef}
          rows={props.bidList}
          columns={columns}
          sortModel={adminBidData.sortModel}
          onSortModelChange={(event) => {
            dispatch(updateSortModel(event));
          }}
          sortingOrder={["asc", "desc"]}
          disableColumnMenu={true}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          selectionModel={selectionModel}
          onCellClick={(params, event) => {
            event.defaultMuiPrevented = true;
            setSelectionModel([params.id]);
          }}
          onRowClick={(params, event) => {
            event.defaultMuiPrevented = true;
          }}
          // hideFooterPagination={true}
          rowHeight={50}
          getRowHeight={(params) =>
            params.model.status === BID_STATUS.CLOSED || params.model.status === BID_STATUS.AWARDED ? 100 : 50
          }
          components={{
            LoadingOverlay: CustomLoadingTableOverlay,
            NoRowsOverlay: CustomNoRowsTableOverlay,
            ErrorOverlay: CustomErrorTableOverlay,
            Toolbar: null,
          }}
          componentsProps={{
            noRowsOverlay: { pageType: BID_ADMIN_PAGE_TYPE },
            errorOverlay: {
              errorMessage: props.errorObject.message,
              errorCode: props.errorObject.code,
            },
            toolbar: { station: props.station },
          }}
          loading={props.isLoading}
          error={props.isError}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              textOverflow: "clip",
              whiteSpace: "break-spaces",
              lineHeight: 1.2,
              textAlign: "left",
            },
          }}
        />
      </div>
    );
  };

  return <Box sx={{ height: `calc(85vh - 150px)` }}>{renderBidTable()}</Box>;
};

BidTable.propTypes = {
  bidList: PropTypes.array.isRequired,
  station: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  tableDesktopRef: PropTypes.object.isRequired,
  setSelectedBidForAuditHistory: PropTypes.func.isRequired,
};

export default BidTable;
